<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { buildPropertyAddressLines, buildPropertyMapLinks } from '../../utils/propertyMapUtils'
import type { PropertyDetail } from '../../types/propertyTypes'

// Where the property is: the postal address as written, and the pin our team
// registered it against. The map is what the seller checks when they want to be
// sure the right unit was listed.
//
// Content only — it renders inside the "Development & location" card, under the
// developer / area facts, rather than as a card of its own repeating them.
const props = defineProps<{ property: PropertyDetail }>()

const addressLines = computed(() => buildPropertyAddressLines(props.property))
const mapLinks = computed(() => buildPropertyMapLinks(props.property))
</script>

<template>
  <Box direction="col" gap="4">
    <Box flex align="start" gap="2">
      <PiIcon name="map-marker" class="location__pin" />
      <Box direction="col" gap="1" class="location__address">
        <Text v-for="line in addressLines" :key="line" type="body">{{ line }}</Text>
      </Box>
    </Box>

    <Box v-if="mapLinks.embedUrl" class="location__map">
      <!-- Third-party map embed: no kit citizen wraps an iframe, and the page
             must not depend on a maps SDK just to draw a pin. -->
      <iframe
        :src="mapLinks.embedUrl"
        class="location__frame"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        title="Property location map"
      />
    </Box>

    <Box
      v-if="mapLinks.mapsUrl"
      is="a"
      :href="mapLinks.mapsUrl"
      target="_blank"
      rel="noopener noreferrer"
      flex
      align="center"
      gap="2"
      class="location__open"
    >
      <PiIcon name="map" class="location__open-icon" />
      <Text type="caption" weight="medium" is="span">Open in Maps</Text>
    </Box>
  </Box>
</template>

<style scoped>
.location__pin {
  flex-shrink: 0;
  margin-top: 0.1875rem;
  font-size: 0.875rem;
  color: var(--p-primary-600);
}

.location__address {
  min-width: 0;
}

.location__map {
  height: 15rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  overflow: hidden;
  background: var(--p-surface-100);
}

.location__frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.location__open {
  align-self: flex-start;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  background: var(--p-surface-0);
  text-decoration: none;
  color: var(--p-text-color);
}

.location__open:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.location__open-icon {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
